import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, extractErrorMessage } from '../../api/client';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { CodigoArticulo } from '../../components/CodigoArticulo';
import { etiquetaArticulo, useIdentidades, type IdentidadArticulo } from '../../lib/identidadArticulos';
import { CANCELADO, REINTENTOS_POR_LIMITE, SIGUE_EL_LIMITE, conEsperaPorLimite, incierto } from '../../api/esperaPorLimite';

/**
 * "Empezar a sincronizar estos SKU" desde el detalle de un conteo confirmado (DECISIONES 2026-10-02): los SKU se cuentan
 * de a grupos y cada grupo contado empieza a sincronizar su stock con Mercado Libre de una vez.
 * Vista previa de todos juntos (ml/sync-sku/preview-lote, no escribe nada) y, al confirmar, el ERP habilita de a UN SKU con
 * el endpoint de siempre ("Empezar a sincronizar", con los packs que mostró la vista previa), así cada uno pasa por los
 * mismos controles; si uno falla sigue con el resto. Un 409 (los packs cambiaron) no se reintenta: se recarga esa fila y
 * queda para revisar. Un 429 (límite de pedidos, DECISIONES 2026-10-10) espera y reintenta el mismo SKU, con tope.
 */

const URL_SYNC = '/api/v3/ml/sync-sku';
/** Tope de SKU por vista previa (MAX_SKUS_PREVIEW_LOTE del backend): más, se piden de a lotes. */
const SKUS_POR_LOTE = 100;
export const URL_SINCRONIZAR_CON_ML = '/base-publicaciones?tab=mercado-libre';

export interface FilaPreviewLote {
  sku: string;
  descripcion: string | null;
  existe: boolean;
  habilitado: boolean;
  contado: boolean;
  esCombo: boolean;
  /** Packs sin sincronizar que se suman al empezar (los que se confirman). */
  packs: Array<{ sku: string; descripcion: string | null; unidadesPorPack: number }>;
  publicaciones: number;
  publicacionesQueSeEscriben: number;
  sePublica: number | null;
  avisos: string[];
  error: string | null;
}

interface ResultadoHabilitar {
  packs?: string[];
  resultado: { enviados: number; fallidos: number };
}

type Resultado = { estado: 'ok' | 'error' | 'revisar'; mensaje: string };

/** Se puede elegir: existe y todavía no sincroniza. */
const elegible = (f: FilaPreviewLote) => f.existe && !f.error && !f.habilitado;

/** Elegido por defecto: contado, sin sincronizar, con alguna publicación que se escribiría y que no es un combo. */
export const elegidoPorDefecto = (f: FilaPreviewLote) =>
  elegible(f) && f.contado && !f.esCombo && f.publicacionesQueSeEscriben > 0;

/** Por qué no viene marcado un SKU que se puede elegir (null si viene marcado). */
export function motivoSinMarcar(f: FilaPreviewLote): string | null {
  if (!elegible(f) || elegidoPorDefecto(f)) return null;
  if (!f.contado) return 'Sin contar';
  if (f.esCombo || (f.publicaciones > 0 && f.publicacionesQueSeEscriben === 0)) return 'Combo: se actualiza cuando sincronizan sus componentes';
  return 'Sin publicaciones para actualizar';
}

const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('es-AR'));
const nombrePacks = (packs: FilaPreviewLote['packs'], identidad: (sku: string) => IdentidadArticulo | undefined) =>
  packs.length === 0 ? '—' : packs.map((p) => `${etiquetaArticulo(p.sku, identidad(p.sku))} (x${p.unidadesPorPack.toLocaleString('es-AR')})`).join(', ');

async function cargarVistaPrevia(skus: string[]): Promise<FilaPreviewLote[]> {
  const filas: FilaPreviewLote[] = [];
  for (let i = 0; i < skus.length; i += SKUS_POR_LOTE) {
    filas.push(...(await api.post<FilaPreviewLote[]>(`${URL_SYNC}/preview-lote`, { skus: skus.slice(i, i + SKUS_POR_LOTE) })));
  }
  return filas;
}

/** "Empezar a sincronizar" un SKU; ante un 429 espera y reintenta (hasta REINTENTOS_POR_LIMITE veces). */
const habilitarConEspera = (fila: FilaPreviewLote, avisar: (esperando: boolean) => void, sigue: () => boolean) =>
  conEsperaPorLimite(
    () => api.post<ResultadoHabilitar>(`${URL_SYNC}/habilitar`, { sku: fila.sku, packs: fila.packs.map((p) => p.sku) }), avisar, sigue,
  );

const COLOR_RESULTADO: Record<Resultado['estado'], string> = { ok: 'text-green-700', error: 'text-red-700', revisar: 'text-amber-700' };

export function SincronizarSkusConteo({ conteoId, skus, onClose }: {
  conteoId: number;
  /** SKU del conteo (sin repetir). */
  skus: string[];
  onClose: () => void;
}) {
  const [filas, setFilas] = useState<FilaPreviewLote[] | null>(null);
  const identidad = useIdentidades((filas ?? []).flatMap((f) => [f.sku, ...f.packs.map((p) => p.sku)]));
  const [error, setError] = useState<string | null>(null);
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [resultados, setResultados] = useState<Record<string, Resultado>>({});
  const [progreso, setProgreso] = useState<{ actual: number; total: number; esperando?: boolean } | null>(null);
  const [resumen, setResumen] = useState<{ ok: number; error: number; revisar: number } | null>(null);
  /** No se pudo recargar la lista después de sincronizar: puede estar desactualizada (no se deja volver a correr). */
  const [desactualizada, setDesactualizada] = useState(false);
  const [recargando, setRecargando] = useState(false);
  const corriendo = progreso !== null;
  /** Al cerrar la ventana (desmontar) se corta la sincronización: no sigue pidiendo ni reintentando. */
  const montado = useRef(true);
  useEffect(() => { montado.current = true; return () => { montado.current = false; }; }, []);
  // Por contenido: otra lista con los mismos SKU (p. ej. al volver a dibujarse el detalle) no recarga la vista previa.
  const claveSkus = skus.join('\n');
  const listaSkus = useMemo(() => (claveSkus ? claveSkus.split('\n') : []), [claveSkus]);

  const cargar = useCallback(async (conservar: Set<string> | null) => {
    try {
      const nuevas = await cargarVistaPrevia(listaSkus);
      setFilas(nuevas);
      setError(null);
      setDesactualizada(false);
      // Al recargar se conserva lo elegido que todavía se puede elegir (lo que ya sincroniza sale solo).
      setElegidos(new Set(nuevas.filter((f) => (conservar ? conservar.has(f.sku) && elegible(f) : elegidoPorDefecto(f))).map((f) => f.sku)));
    } catch (e) {
      if (conservar) setDesactualizada(true);
      else setError(extractErrorMessage(e));
    }
  }, [listaSkus]);

  const recargar = async () => {
    setRecargando(true);
    await cargar(elegidos);
    setRecargando(false);
  };

  useEffect(() => { void cargar(null); }, [cargar]);

  const alternar = (sku: string) => setElegidos((previos) => {
    const siguientes = new Set(previos);
    if (siguientes.has(sku)) siguientes.delete(sku);
    else siguientes.add(sku);
    return siguientes;
  });

  const aSincronizar = (filas ?? []).filter((f) => elegible(f) && elegidos.has(f.sku));

  /** De a un SKU, en orden: cada uno con el endpoint de "Empezar a sincronizar" y sus packs confirmados. */
  const sincronizar = async () => {
    if (corriendo || desactualizada || aSincronizar.length === 0) return;
    const lista = aSincronizar;
    const cuenta = { ok: 0, error: 0, revisar: 0 };
    setResumen(null);
    setResultados({});
    for (const [i, fila] of lista.entries()) {
      if (!montado.current) return;
      setProgreso({ actual: i + 1, total: lista.length });
      let resultado: Resultado;
      try {
        const r = await habilitarConEspera(
          fila, (esperando) => setProgreso({ actual: i + 1, total: lista.length, esperando }), () => montado.current,
        );
        const packs = r.packs ?? [];
        resultado = {
          estado: 'ok',
          mensaje: `Sincronizando: ${n(r.resultado.enviados)} publicaciones enviadas`
            + `${packs.length > 0 ? ` (con ${packs.join(', ')})` : ''}`
            + `${r.resultado.fallidos > 0 ? `, ${n(r.resultado.fallidos)} con error (se reintenta sola)` : ''}`,
        };
      } catch (e) {
        if (e === CANCELADO) return;
        if (e === SIGUE_EL_LIMITE) {
          resultado = {
            estado: 'error',
            mensaje: `Error: el servidor siguió pidiendo esperar (límite de pedidos) después de ${REINTENTOS_POR_LIMITE} reintentos; no se sincronizó, volvé a intentarlo más tarde`,
          };
        } else if ((e as { status?: number } | null)?.status === 409) {
          // Los packs cambiaron desde la vista previa: no se encendió nada. Se recarga su fila y queda para revisar.
          let ahora = '';
          try {
            const [nueva] = await cargarVistaPrevia([fila.sku]);
            if (nueva) {
              setFilas((previas) => (previas ?? []).map((f) => (f.sku === fila.sku ? nueva : f)));
              ahora = ` (ahora suma ${nueva.packs.length > 0 ? nombrePacks(nueva.packs, identidad) : 'ningún pack'})`;
            }
          } catch { /* sin la fila nueva igual queda para revisar */ }
          resultado = { estado: 'revisar', mensaje: `Revisar: los packs cambiaron desde la vista previa${ahora}; revisalo y volvé a sincronizarlo` };
        } else {
          resultado = {
            estado: 'error',
            mensaje: `Error: ${extractErrorMessage(e)}${incierto(e) ? ' (puede haberse aplicado igual: revisá el estado)' : ''}`,
          };
        }
      }
      cuenta[resultado.estado]++;
      setResultados((previos) => ({ ...previos, [fila.sku]: resultado }));
    }
    if (!montado.current) return;
    setProgreso(null);
    setResumen(cuenta);
    await cargar(new Set(lista.map((f) => f.sku)));
  };

  const textoResumen = resumen && [
    `${resumen.ok} ${resumen.ok === 1 ? 'empezó' : 'empezaron'} a sincronizar`,
    ...(resumen.error > 0 ? [`${resumen.error} con error`] : []),
    ...(resumen.revisar > 0 ? [`${resumen.revisar} para revisar`] : []),
  ].join(' · ');
  const conResultados = Object.keys(resultados).length > 0;

  return (
    <Modal open onClose={() => { if (!corriendo) onClose(); }} title={`Empezar a sincronizar estos SKU · Conteo #${conteoId}`} size="full">
      <div className="space-y-3 text-sm">
        <p className="text-slate-600">
          Elegí los SKU que empiezan a sincronizar su stock con Mercado Libre (cada uno con sus packs). Se habilitan de a uno,
          como con &quot;Empezar a sincronizar&quot; en la vista previa del SKU en Mercado Libre (Base y publicaciones); si uno falla, sigue con el resto. El detalle de cada
          publicación está en la vista previa de cada SKU.
        </p>
        <p className="text-slate-600">Vienen marcados los SKU contados, con publicaciones para actualizar y que todavía no sincronizan.</p>
        {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">No se pudo cargar la vista previa: {error}</p>}
        {!filas && !error && <p className="text-slate-500">Cargando la vista previa…</p>}
        {desactualizada && (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
            <span>No se pudo actualizar la lista; cerrá y volvé a abrir</span>
            <Button size="sm" variant="ghost" onClick={() => void recargar()} loading={recargando}>Actualizar la lista</Button>
          </div>
        )}
        {filas && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-1 pr-3 font-medium"><span className="sr-only">Elegir</span></th>
                  <th className="py-1 pr-3 font-medium">SKU</th>
                  <th className="py-1 pr-3 font-medium">Descripción</th>
                  <th className="py-1 pr-3 font-medium text-right" title="Publicaciones suyas y de packs/combos que lo contienen">Publicaciones</th>
                  <th className="py-1 pr-3 font-medium text-right" title="Lo que se publicaría al sincronizarlo (con su stock de resguardo)">Se publica</th>
                  <th className="py-1 pr-3 font-medium">Packs que se suman</th>
                  <th className="py-1 pr-3 font-medium">Avisos</th>
                  {conResultados && <th className="py-1 pr-3 font-medium">Resultado</th>}
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => {
                  const r = resultados[f.sku];
                  const motivo = elegidos.has(f.sku) ? null : motivoSinMarcar(f);
                  return (
                    <tr key={f.sku} className={`border-b border-slate-100 align-top ${elegible(f) ? '' : 'text-slate-400'}`}>
                      <td className="py-1 pr-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          aria-label={`Sincronizar ${etiquetaArticulo(f.sku, identidad(f.sku))}`}
                          checked={elegible(f) && elegidos.has(f.sku)}
                          disabled={!elegible(f) || corriendo}
                          onChange={() => alternar(f.sku)}
                        />
                        {f.habilitado && <Badge tone="slate" className="ml-2">Ya sincroniza</Badge>}
                        {motivo && <span className="block text-xs text-slate-500">{motivo}</span>}
                      </td>
                      <td className="py-1 pr-3"><CodigoArticulo sku={f.sku} /></td>
                      <td className="py-1 pr-3">{f.descripcion ?? '—'}</td>
                      <td className="py-1 pr-3 text-right">{n(f.publicaciones)}</td>
                      <td className="py-1 pr-3 text-right">{n(f.sePublica)}</td>
                      <td className="py-1 pr-3">{nombrePacks(f.packs, identidad)}</td>
                      <td className="py-1 pr-3">
                        {(f.error || f.avisos.length > 0) ? (
                          <ul className="space-y-0.5 text-xs">
                            {f.error && <li className="text-red-700">{f.error}</li>}
                            {f.avisos.map((a) => <li key={a} className={elegible(f) ? 'text-amber-700' : ''}>{a}</li>)}
                          </ul>
                        ) : '—'}
                      </td>
                      {conResultados && <td className={`py-1 pr-3 text-xs ${r ? COLOR_RESULTADO[r.estado] : ''}`}>{r?.mensaje ?? ''}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {progreso && (
          <p role="status" className="text-slate-700">
            <span>{`Sincronizando ${progreso.actual} de ${progreso.total}…`}</span>
            {progreso.esperando && <span className="block text-amber-700">Esperando para seguir (límite de pedidos)…</span>}
            <span className="block text-xs text-slate-500">No cierres esta ventana hasta que termine.</span>
          </p>
        )}
        {textoResumen && (
          <div role="status" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">
            <p className="font-medium">{`Listo: ${textoResumen}.`}</p>
            <p>
              <Link to={URL_SINCRONIZAR_CON_ML} className="text-brand-700 underline">Ir a Mercado Libre (Base y publicaciones)</Link>
            </p>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-3 pt-3 border-t border-slate-200">
          <Button variant="ghost" onClick={onClose} disabled={corriendo}>Cerrar</Button>
          {filas && (
            <Button onClick={() => void sincronizar()} disabled={aSincronizar.length === 0 || desactualizada} loading={corriendo}>
              {`Sincronizar ${aSincronizar.length} SKU`}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
